import React from "react";
import { useNavigate } from "react-router-dom";

import { Label } from "@fronterahealth/frontera-ui-components";

import { ApiVideoSessionSessionStatusChoices } from "@api/graphql/types-and-hooks";
import { FeedMediaCardEmojiPanel } from "@pages/VideoSessionsV2/components/Media/FeedMediaCardEmojiPanel";
import { FeedMediaCardHeader } from "@pages/VideoSessionsV2/components/Media/FeedMediaCardHeader";
import { FeedMediaCardMomentContent } from "@pages/VideoSessionsV2/components/Media/FeedMediaCardMomentContent";
import { FeedMediaCardThumbnail } from "@pages/VideoSessionsV2/components/Media/FeedMediaCardThumbnail";
import { MediaCardType } from "@pages/VideoSessionsV2/components/MediaCards";
import { displayDate, formatDate } from "@pages/VideoSessionsV2/components/Utils/formatDate";
import { NO_MEDIA_STATUS } from "@pages/VideoSessionsV2/const";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface FeedMediaCardProps {
  media?: MediaCardType;
}

export const FeedMediaCard: React.FC<FeedMediaCardProps> = ({ media }) => {
  const navigate = useNavigate();

  const { isUserCaregiver } = useAdminData();

  const { sidebarMomentsList } = useVideoSessionData();

  if (!media || !media.videoSession) return null;
  const thumbnailUrl = media.thumbnailManifest?.thumbnails?.[0]?.url || null;

  const learnerName = media.videoSession.sessionlearnerSet?.edges[0]?.node?.sessionLearners?.learnermetadata;
  const learnerFullName = learnerName ? `${learnerName.firstName} ${learnerName.lastName}` : "";
  const providerMeta = media.videoSession.sessionproviderSet?.edges[0]?.node?.provider?.providermetadata;
  const caregiverMeta = media.videoSession.sessioncaregiverSet?.edges[0]?.node?.caregiver?.caregivermetadata;
  const isMissingMedia = NO_MEDIA_STATUS.includes(
    (media.videoSession?.sessionStatus as ApiVideoSessionSessionStatusChoices) ?? "",
  );
  const sessionDate = new Date(media.videoSession.createdDate);
  const formattedDate = isMissingMedia ? formatDate(sessionDate) : displayDate(media.totalDuration ?? 0, sessionDate);

  const showMomentsArea = sidebarMomentsList.some((moment) => moment.isVisible) && !isUserCaregiver;

  const isMediaDetailRedirect: boolean =
    media && media.videoSession?.sessionStatus === ApiVideoSessionSessionStatusChoices.Completed;

  const sessionRecorder = caregiverMeta || providerMeta;

  return (
    <div
      className={`w-full border border-limestone-200 rounded-lg bg-white p-3 flex flex-col drop-shadow-xs gap-2 ${isMediaDetailRedirect ? "cursor-pointer" : "cursor-default"}  hover:shadow-[0px_8px_12px_rgba(145,158,171,0.25)] duration-200`}
      onClick={() => {
        if (isMediaDetailRedirect) {
          navigate(`/video-sessions-v2/${media.id}`);
        }
      }}
    >
      <FeedMediaCardHeader
        learnerFullName={learnerFullName}
        providerLastName={sessionRecorder?.lastName ?? ""}
        providerFirstName={sessionRecorder?.firstName ?? ""}
        date={formattedDate}
        sessionRating={media.videoSession?.sessionRating ?? undefined}
        media={media}
        isMissingMedia={isMissingMedia}
      />

      <FeedMediaCardThumbnail
        status={(media.videoSession?.sessionStatus as ApiVideoSessionSessionStatusChoices) ?? ""}
        thumbnailUrl={thumbnailUrl}
      />

      <div className="justify-between flex flex-row items-center">
        <Label
          displayType="loud"
          size="extra-small"
          className={`${isMissingMedia ? "opacity-25" : showMomentsArea ? "opacity-100" : "opacity-0"}`}
          colorType="secondary"
        >
          Moments
        </Label>

        <FeedMediaCardEmojiPanel commentCount={media.commentsCount} disabled={isMissingMedia} />
      </div>
      {showMomentsArea ? <FeedMediaCardMomentContent media={media} /> : null}
    </div>
  );
};
